import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, Path, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { FormWorkflowNode } from './FormWorkflowNode';
import { IndicatorType } from '../components/WorkflowIndicator';
import { useDebounce } from '../hooks/useDebounce';

export type WorkflowNode<T extends object = object> = {
  element: React.ReactElement | null;
  validationSchema?: Partial<Record<keyof T, Yup.AnySchema>>;
  breakRenderingOnInvalid?: boolean;
  title?: string;
  indicatorType?: IndicatorType;
  classname?: string;
  name?: Path<T>;
  children?: (formValues: T) => Omit<WorkflowNode<T>, 'children' | 'validationSchema'> | null | undefined;
};

interface FormWorkflowViewProps<T extends object> {
  nodes: WorkflowNode<T>[];
  initialValues?: Partial<T>;
  onSubmit: (values: T) => void;
}

export const FormWorkflowView = <T extends object>({ nodes, onSubmit, initialValues }: FormWorkflowViewProps<T>) => {
  const [traversedNodeList, setTraversedNodeList] = useState<(Omit<WorkflowNode<T>, 'validationSchema'> & { nodeIndex: number })[]>([]);
  const [validationSchema, setValidationSchema] = useState<Partial<Record<keyof T, Yup.AnySchema>>[]>([]);
  const [currentNodeIndex, setCurrentNodeIndex] = useState<number>(-1);

  const mergedValidationSchema: Partial<Record<keyof T, Yup.AnySchema>> = useMemo(() => {
    return validationSchema.reduce<Partial<Record<keyof T, Yup.AnySchema>>>((acc, schema) => {
      return { ...acc, ...schema };
    }, {});
  }, [validationSchema]);

  const methods = useForm<T>({
    values: initialValues as T,
    mode: 'onChange',
    // @ts-expect-error: It errrors here because the types don't necassarily line up, but it does work
    resolver: yupResolver<T>(Yup.object<T>(mergedValidationSchema))
  });

  const debouncedIsFormValid = useDebounce(methods.formState.isValid, 200);

  const traverse = () => {
    let nextNodeIndex = currentNodeIndex + 1;
    let nextNode = nodes[nextNodeIndex];
    if (!nextNode) return;

    let validNow = true;
    while (nextNode && validNow) {
      const node = nextNode;
      const { validationSchema: nodeValidationSchema, ...rest } = node;

      if (node.element === null) {
        nextNodeIndex += 1;
        nextNode = nodes[nextNodeIndex];
        continue;
      }

      validNow = Yup.object<T>({
        ...mergedValidationSchema,
        ...nodeValidationSchema
      }).isValidSync(methods.getValues());

      setTraversedNodeList(prev => [...prev, { ...rest, nodeIndex: nextNodeIndex }]);
      // validation schema state is an array of partial schemas that get merged together after
      if (nodeValidationSchema) {
        setValidationSchema(prev => [...prev, nodeValidationSchema]);
      }

      if (validNow) {
        nextNodeIndex += 1;
        nextNode = nodes[nextNodeIndex];
      }
    }
    setCurrentNodeIndex(nextNodeIndex);
  };

  useEffect(() => {
    if (!debouncedIsFormValid) return;
    traverse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedIsFormValid]);

  const handleNodeError = (traversedIndex: number, nodeIndex: number) => {
    setTraversedNodeList(prev => prev.slice(0, traversedIndex + 1));
    setValidationSchema(prev => prev.slice(0, traversedIndex + 1));
    setCurrentNodeIndex(nodeIndex);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        {traversedNodeList.map((node, index) => (
          <FormWorkflowNode key={index} workflowNode={node} index={index} totalNodes={traversedNodeList.length} name={node.name} handleNodeError={handleNodeError} />
        ))}
      </form>
    </FormProvider>
  );
};
